import {
  ALPHABET,
  ALPHABET_SIZE,
  letterIndex,
  lettersOnly,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  type CipherDirection,
  type KeyedRun,
  type KeyedStep,
  runKeyedLetters,
} from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";

/**
 * The Vigenère autokey cipher.
 *
 * A Vigenère cipher whose key does not repeat. The sender agrees a short **primer**; the
 * key is the primer followed by the message itself, so the key is as long as the text:
 *
 *     plain text  ATTACKATDAWN
 *     key         QUEENLYATTAC   (primer QUEENLY, then the plain text itself)
 *     cipher      QNXEPVYTWTWP
 *
 * With A = 0 … Z = 25, each cipher letter is C = P + K (mod 26). Decoding is
 * P = C − K, and each plain letter recovered is appended to the key, so the receiver
 * always has the key letter he needs by the time he needs it.
 *
 * - **Plain-text autokey** is the classical form (Cardano's idea, perfected by
 *   Vigenère in 1586) and the American Cryptogram Association's "Autokey".
 * - **Cipher-text autokey** takes the cipher text it has just produced as the key
 *   instead. It is the other form found in the textbooks; it needs no plain letters to
 *   decode, so the whole key is in the cipher text for anyone to read.
 */

export type AutokeySource = "plain" | "cipher";

/** The 26 × 26 Vigenère square: row = key letter, column = plain letter, cell = cipher. */
export function vigenereSquare(): string[] {
  const cells: string[] = [];
  for (let key = 0; key < ALPHABET_SIZE; key += 1) {
    for (let plain = 0; plain < ALPHABET_SIZE; plain += 1) {
      cells.push(ALPHABET.charAt(mod(key + plain, ALPHABET_SIZE)));
    }
  }
  return cells;
}

export interface AutokeyResult extends KeyedRun {
  /** The primer's letters in capitals — empty when the primer has none. */
  primer: string;
  /** The whole key, one letter per enciphered letter. */
  keyStream: string;
}

export function autokeyTransform(
  text: string,
  primerText: string,
  source: AutokeySource,
  direction: CipherDirection
): AutokeyResult {
  const primer = lettersOnly(primerText);
  if (primer === "") {
    return { primer, keyStream: "", output: "", steps: [] };
  }
  // Letters that extend the key after the primer: plain letters, or cipher letters.
  const feed: string[] = [];
  const run = runKeyedLetters(text, direction, (letter, position) => {
    const keyLetter =
      position < primer.length
        ? primer.charAt(position)
        : (feed.at(position - primer.length) ?? "");
    const key = letterIndex(keyLetter);
    const value = letterIndex(letter);
    let step: KeyedStep;
    if (direction === "encode") {
      step = {
        plain: letter,
        key: keyLetter,
        cipher: ALPHABET.charAt(mod(value + key, ALPHABET_SIZE)),
        calc: { left: value, op: "+", right: key, raw: value + key },
      };
    } else {
      step = {
        plain: ALPHABET.charAt(mod(value - key, ALPHABET_SIZE)),
        key: keyLetter,
        cipher: letter,
        calc: { left: value, op: "−", right: key, raw: value - key },
      };
    }
    feed.push(source === "plain" ? step.plain : step.cipher);
    return step;
  });
  return {
    primer,
    keyStream: run.steps.map((step) => step.key).join(""),
    ...run,
  };
}
